import { buildCategoryNavigationPath } from "@faire/retailer-shared/categories/navigationPath";
import { CATEGORY_PATH_IDENTIFIER_DELIMITER } from "@faire/retailer-shared/categories/pathIdentifier";
import {
  ALL_BRANDS_URL_ID,
  ECO_FRIENDLY_SUBCATEGORY_URL_ID,
  NEW_ARRIVALS_SUBCATEGORY_URL_ID,
  NEW_BRANDS_CATEGORY_URL_ID,
  NEW_PRODUCTS_CATEGORY_URL_ID,
  PLUS_SIZE_CATEGORY_URL_ID,
  SHOWROOM_CATEGORY_URL_ID,
} from "@faire/retailer-shared/categories/urlId";
import { IUrlId } from "@faire/retailer-visitor-shared/models/Category/IUrlId";

/**
 * Wraps a full category path (e.g. "Women's|Apparel|Jeans") into a type-safe
 * wrapper that has knowledge of the full category path but never leaks
 * that information outside of this class.
 */
interface CategoryModelParams {
  name: string;
  urlId: IUrlId;
  pathIdentifier: string | undefined;
  isProductFirstCategory?: boolean | undefined;
  subCategory?: Category;
  uniqueDisplayName?: string;
}
export class Category {
  readonly name: string;

  // The URL ID of the category. This is used to look up a category based on the route segments
  // for a category route
  readonly urlId: IUrlId;

  // A path identifier on a category is a unique value used by the backend to identify a category.
  readonly pathIdentifier: string | undefined;

  readonly subCategory: Category | undefined;

  private readonly uniqueDisplayName: string | undefined;

  readonly isProductFirstCategory: boolean | undefined;

  constructor({
    name,
    urlId,
    pathIdentifier,
    isProductFirstCategory,
    subCategory,
    uniqueDisplayName,
  }: CategoryModelParams) {
    this.name = name;
    this.urlId = urlId;
    this.pathIdentifier = pathIdentifier;
    this.subCategory = subCategory;
    this.uniqueDisplayName = uniqueDisplayName;
    this.isProductFirstCategory = isProductFirstCategory;
  }

  get mostSpecificCategory(): Category {
    return this.subCategory?.mostSpecificCategory ?? this;
  }

  get mostSpecificName(): string {
    return this.subCategory?.mostSpecificName ?? this.name;
  }

  get mostSpecificUniqueDisplayName(): string | undefined {
    return (
      this.subCategory?.mostSpecificUniqueDisplayName ?? this.uniqueDisplayName
    );
  }

  get mostSpecificPathIdentifier(): string | undefined {
    return this.subCategory?.mostSpecificPathIdentifier ?? this.pathIdentifier;
  }

  // TODO: This should leverage the tree_level property from the backend instead of parsing
  // the path identifier
  get mostSpecificLevel(): number {
    const categories =
      this.mostSpecificPathIdentifier?.split(
        CATEGORY_PATH_IDENTIFIER_DELIMITER
      ) ?? [];

    return categories.length;
  }

  get urlIds(): [IUrlId, ...IUrlId[]] {
    return [this.urlId, ...(this.subCategory?.urlIds ?? [])];
  }

  get urlIdStrings(): string[] {
    return this.urlIds.map((urlId) => urlId.id);
  }

  get navigationPath(): string {
    return buildCategoryNavigationPath(this.urlIdStrings);
  }

  get isSuppliersPageCategoryValid(): boolean {
    // these categories do not have a suppliers page
    return !(
      this.isNewProducts ||
      this.isNewBrandsC1 ||
      this.isNewArrivalsSubCategory
    );
  }

  get isPrimaryCategory(): boolean {
    return !this.subCategory;
  }

  get isAllBrands(): boolean {
    return this.urlId.id === ALL_BRANDS_URL_ID;
  }

  get isPlusSize(): boolean {
    return this.urlId.id === PLUS_SIZE_CATEGORY_URL_ID;
  }

  get isNewProducts(): boolean {
    return this.isNewProductsSubCategory || this.isNewProductsC1;
  }

  get isNewProductsSubCategory(): boolean {
    // return true if the url is /category/{c1CategoryUrlId}/subcategory[/{c2CategoryUrlId}]/New Products
    return (
      (this.subCategory?.isNewProducts &&
        this.urlId.id !== NEW_PRODUCTS_CATEGORY_URL_ID) ??
      false
    );
  }

  get isNewProductsC1(): boolean {
    // return true if the url is
    // /category/new products[/subcategory/{c2categoryUrlId}/[/{c3CategoryUrlId}]]
    return this.urlId.id === NEW_PRODUCTS_CATEGORY_URL_ID;
  }

  get isNewBrandsC1(): boolean {
    return this.urlId.id === NEW_BRANDS_CATEGORY_URL_ID;
  }

  get isNewArrivalsSubCategory(): boolean {
    return (
      this.subCategory?.isNewArrivalsSubCategory ||
      this.urlId.id === NEW_ARRIVALS_SUBCATEGORY_URL_ID
    );
  }

  get isEcoFriendlySubCategory(): boolean {
    return (
      this.subCategory?.isEcoFriendlySubCategory ||
      this.urlId.id === ECO_FRIENDLY_SUBCATEGORY_URL_ID
    );
  }

  get isSubcategoryOfShowroomC1(): boolean {
    return this.urlId.id === SHOWROOM_CATEGORY_URL_ID;
  }
}
